import React, { useState } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { KendoReactFileUpload } from '@Shared.Angular/kendo.react/KendoReactFileUpload';
import { getService } from '@Client/runner.hooks/useService';
import ICreateProcedure from '@Shared.Angular/@types/core/contracts/commands/procedure/createProcedure';
import Chip from '@Shared.Angular/flowingly.components/Chip/Chip';
import { MAIN_SPINNER_ID } from '@Client/runner.spinInterceptor';

type Props = {
  onClose: () => void;
};

enum ImportStatus {
  None = 'none',
  Imported = 'imported',
  Failed = 'failed',
  AlreadyImported = 'alreadyImported'
}

type ImportStatusMap = Record<string, ImportStatus>;

type IImportableProcedure = {
  key: string;
  listKey: string;
  procedure: ICreateProcedure;
  isDuplicate: boolean;
};

const ALREADY_IMPORTED_MESSAGE = 'There is already a Procedure named';

const normalizeText = (value?: string): string => value?.trim() ?? '';

const buildProcedureKey = (procedure: ICreateProcedure): string => {
  return normalizeText(procedure.name).toLowerCase();
};

const getImportErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (!error || typeof error !== 'object') {
    return '';
  }

  const errorWithData: { data?: { message?: string } } = error as {
    data?: { message?: string };
  };
  if (errorWithData.data?.message) {
    return errorWithData.data?.message ?? '';
  }

  const errorWithMessage: { message?: string } = error as { message?: string };
  return errorWithMessage.message ?? '';
};

export const ProcedureBulkImportModal = (props: Props) => {
  const { onClose } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [importables, setImportables] = useState<IImportableProcedure[]>([]);

  const [importStatusByProcedureKey, setImportStatus] =
    useState<ImportStatusMap>({});
  const [isImporting, setIsImporting] = useState(false);
  const spinnerService = getService('usSpinnerService');

  const importProcedures = async (): Promise<void> => {
    setIsImporting(true);
    const procedureApiService = getService('procedureApiService');
    const importableProcedures: IImportableProcedure[] = importables.filter(
      (importable: IImportableProcedure) => !importable.isDuplicate
    );
    for (const importable of importableProcedures) {
      const currentStatus: ImportStatus | undefined =
        importStatusByProcedureKey[importable.key];
      if (
        currentStatus === ImportStatus.Imported ||
        currentStatus === ImportStatus.AlreadyImported
      ) {
        continue;
      }
      try {
        await procedureApiService.importProcedure(importable.procedure);
        setImportStatus((prevStates) => ({
          ...prevStates,
          [importable.key]: ImportStatus.Imported
        }));
      } catch (error) {
        const errorMessage: string = getImportErrorMessage(error);
        if (errorMessage.includes(ALREADY_IMPORTED_MESSAGE)) {
          setImportStatus((prevStates) => ({
            ...prevStates,
            [importable.key]: ImportStatus.AlreadyImported
          }));
          continue;
        }
        console.error(
          `Failed to import procedure ${importable.procedure.name}: `,
          error
        );
        setImportStatus((prevStates) => ({
          ...prevStates,
          [importable.key]: ImportStatus.Failed
        }));
      }
    }
    setIsImporting(false);
  };

  const parseNewFiles = async (incomingFiles: File[]): Promise<void> => {
    const newFiles: File[] = incomingFiles.filter((newFile: File) => {
      return !files.some(
        (existingFile: File) => existingFile.name === newFile.name
      );
    });
    if (newFiles.length === 0) {
      return;
    }

    setFiles((prevFiles: File[]) => [...prevFiles, ...newFiles]);
    spinnerService.spin(MAIN_SPINNER_ID);
    const importablePromises: Promise<ICreateProcedure>[] = getService(
      'procedureImportService'
    ).parseScribeHtmlFiles(newFiles);
    Promise.all(importablePromises)
      .then((newImportables: ICreateProcedure[]) => {
        setImportables((prevImportables: IImportableProcedure[]) => {
          const existingKeys: Set<string> = new Set(
            prevImportables
              .filter(
                (importable: IImportableProcedure) => !importable.isDuplicate
              )
              .map((importable: IImportableProcedure) => importable.key)
          );
          const updatedImportables: IImportableProcedure[] = [
            ...prevImportables
          ];

          newImportables.forEach(
            (procedure: ICreateProcedure, index: number) => {
              const key: string = buildProcedureKey(procedure);
              const isDuplicate: boolean = existingKeys.has(key);
              updatedImportables.push({
                key,
                listKey: `${key}-${index}-${updatedImportables.length}`,
                procedure,
                isDuplicate
              });
              if (!isDuplicate) {
                existingKeys.add(key);
              }
            }
          );

          return updatedImportables;
        });
      })
      .catch((error) => {
        console.error('Failed to parse files:', error);
        getService('notificationService').showErrorToast(
          'Failed to parse one or more files'
        );
      })
      .finally(() => {
        spinnerService.stop(MAIN_SPINNER_ID);
      });
  };

  return (
    <Modal
      id="procedure-bulk-import-modal"
      title={'Bulk Import SOPs'}
      actionButtonText="Import"
      isActionDisabled={importables.length === 0 || isImporting}
      onActionClick={importProcedures}
      onClose={onClose}
      onCancelClick={onClose}
      cancelButtonText="Close"
      size="lg"
    >
      <div>
        <KendoReactFileUpload
          acceptedFileExtensions={['.html']}
          multiple={true}
          selectButtonText="Upload Scribe HTML exports"
          onFilesAdded={parseNewFiles}
          dataTestId="procedure-bulk-import-file-upload"
        />
        {importables.length > 0 && (
          <div>
            <div>Loaded SOPs:</div>
            <ul>
              {importables.map((importable: IImportableProcedure) => (
                <li key={importable.listKey}>
                  <div>
                    <span
                      className={
                        importable.isDuplicate
                          ? 'procedure-bulk-import__duplicate-text'
                          : undefined
                      }
                    >
                      {importable.procedure.name}
                    </span>
                    {importable.isDuplicate && (
                      <span className="procedure-bulk-import__duplicate-note">
                        {' '}
                        (duplicate)
                      </span>
                    )}
                  </div>
                  {!importable.isDuplicate &&
                    importStatusByProcedureKey[importable.key] ===
                      ImportStatus.Imported && (
                      <Chip name="Imported" color="0B0" />
                    )}
                  {!importable.isDuplicate &&
                    importStatusByProcedureKey[importable.key] ===
                      ImportStatus.AlreadyImported && (
                      <Chip name="Already imported" color="E6A700" />
                    )}
                  {!importable.isDuplicate &&
                    importStatusByProcedureKey[importable.key] ===
                      ImportStatus.Failed && <Chip name="Failed" color="B00" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
